import { rem } from 'polished';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Event, BigPictureClient } from '@vimeo/bigpicture-client';
import { translate } from '@vimeo/i18n-runtime';
import { blue, white } from '@vimeo/iris/color';
import { Button } from '@vimeo/iris/components';
import { DismissX } from '@vimeo/iris/icons';
import { TeamUserPermissionLevel } from '@vimeo/viewer';

interface Props {
  teamUserRole: string;
  userId: number;
  locale: string;
  vuid: string;
}

export default function CreatePageTeamMemberPrivilegesBanner({
  teamUserRole,
  userId,
  locale,
  vuid,
}: Props) {
  const hasCreationPermission =
    TeamUserPermissionLevel.Contributor === teamUserRole ||
    TeamUserPermissionLevel.ContributorPlus === teamUserRole ||
    TeamUserPermissionLevel.Admin === teamUserRole;
  const bpParams = useRef({
    notification_name: hasCreationPermission
      ? 'teams_connected_to_teams_account'
      : 'teams_no_create_permission',
    product: 'Vimeo Create',
    location: 'template_gallery',
    path: window.location.pathname,
    session_id: null,
    client_timestamp: null,
    platform: window.navigator.platform,
    user_id: `${userId}`,
    vsid: null,
    user_package_type: null,
    third_party_integration: 'none',
    flow: 'create_homepage',
    device_id: vuid,
    clients_session_start_ts: null,
    client_ver: null,
    debug_mode: null,
    in_background: null,
    is_guest: null,
    lang: locale,
  });
  const [showBanner, setShowBanner] = useState(true);
  const removeBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  useEffect(() => {
    if (showBanner) {
      BigPictureClient.sendEvent(
        new Event('view_notification', 1, {
          ...bpParams.current,
        }),
      );
    }
  }, [showBanner, bpParams]);

  const trackTeamSettingsClick = useCallback(() => {
    BigPictureClient.sendEvent(
      new Event('clicked_on_notification', 1, {
        cta: 'teams_settings',
        ...bpParams.current,
      }),
    );
  }, [bpParams]);

  return (
    <>
      {showBanner ? (
        <Wrapper>
          <TextContent largeSize={!hasCreationPermission}>
            {hasCreationPermission ? (
              <>
                <div>{translate("You're creating videos under a team account.")}</div>
                <div>
                  {translate(
                    'You can switch accounts if you want to create videos under your personal account.',
                  )}
                </div>
              </>
            ) : (
              translate(
                'Videos you create will be saved to your personal account. To automatically share created videos with your team, contact the team owner.',
              )
            )}
          </TextContent>
          {hasCreationPermission && (
            <ButtonWrapper href="/manage/videos" onClick={trackTeamSettingsClick}>
              <Button size="xs" style={{ padding: `0 ${rem(14)}`, marginLeft: `${rem(16)}` }}>
                {translate('Switch account')}
              </Button>
            </ButtonWrapper>
          )}
          <DismissIcon onClick={removeBanner} />
        </Wrapper>
      ) : null}
    </>
  );
}

const Wrapper = styled.div`
  height: ${rem(56)};
  background: ${blue(600)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContent = styled.div<{ largeSize?: boolean }>`
  color: ${white};
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  letter-spacing: -${rem(0.2)};
  text-align: left;
  max-width: ${({ largeSize }) => (largeSize ? rem(580) : rem(528))};
`;

const ButtonWrapper = styled.a`
  text-decoration: none;
  outline: none;
`;

const DismissIcon = styled(DismissX)`
  width: ${rem(20)};
  height: ${rem(20)};
  position: absolute;
  right: ${rem(20)};
  top: ${rem(20)};
  cursor: pointer;
  path {
    fill: ${white};
  }
`;
